<template>
  <PropertyDetailsCard>
    <template #title>Photos</template>
    <template #action>
      <PropertyDetailsCardEditButton @click="showEditModal" v-if="images.length" />
    </template>

    <div class="images-wrapper">
      <div class="images-container">
        <PropertyImage
          v-for="(image, index) in images"
          :key="index"
          :image="image"
          :is-cover="index === 0"
          :is-large="index === 0"
          @click="fileViewerModalRef?.show(index)"
        />
      </div>
    </div>

    <PropertyDetailsCardPlaceholder
      v-if="!images.length"
      title="You haven't added any photos yet"
      subtitle="Upload photos to highlight your property's best features"
      button-text="Add photos"
      @button-clicked="showEditModal"
    />

    <PropertyImagesModal ref="propertyImagesModalRef" />

    <FileViewerModal :files="images" ref="fileViewerModalRef">
      <template #actions="{ activeFile }">
        <IconButton variant="secondary" size="medium" shape="square" icon="trash" @click="deleteFile(activeFile.id)" />
      </template>
    </FileViewerModal>
  </PropertyDetailsCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { IconButton } from '@tallo/design-system';
import { FileViewerModal } from '@tallo/file-manager';
import { usePropertyStore } from '@tallo/investor/property';
import {
  PropertyDetailsCard,
  PropertyDetailsCardEditButton,
  PropertyDetailsCardPlaceholder,
  PropertyImage,
} from '@tallo/property';

import PropertyImagesModal from './property-images-modal.vue';

const propertyStore = usePropertyStore();

const images = computed(() => propertyStore.images);
const propertyImagesModalRef = ref<InstanceType<typeof PropertyImagesModal>>();
const fileViewerModalRef = ref<InstanceType<typeof FileViewerModal>>();

function showEditModal() {
  propertyImagesModalRef.value?.modalRef?.show();
}

async function deleteFile(fileId: string) {
  await propertyStore.deleteImage(fileId);
}
</script>

<style scoped lang="scss">
.images-wrapper {
  width: 100%;
  scroll-behavior: smooth;
  align-items: initial;

  -ms-overflow-style: none;
  scrollbar-width: none;
  overflow-y: hidden;
  overflow-x: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  .images-container {
    display: flex;
    flex-direction: row;
    border-radius: var(--t-border-radius-sm);
    gap: 0.375rem;
    flex-wrap: nowrap;
    width: fit-content;
  }

  ::-webkit-scrollbar {
    width: 0;
  }

  * {
    scrollbar-width: none;
  }
}
</style>
